import React, { useState, useEffect } from 'react';
import {
  Search, Filter, Download, Eye, TrendingUp, TrendingDown,
  Calendar, DollarSign, Target, Users, BarChart3, ArrowLeft,
  CheckCircle, XCircle, Clock, Zap, PieChart, Plus, Edit2,
  Trash2, Play, Pause, RefreshCw, Send, Copy, AlertCircle
} from 'lucide-react';
import { apiCallJson } from './Api';
import { getCurrentToken } from './SessionManager';

const CampaignDashboard = ({ sessionId, user, onNavigate }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [executing, setExecuting] = useState(new Set());

  useEffect(() => {
    loadData();
  }, [sessionId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, segmentsRes] = await Promise.all([
        apiCallJson(`/api/campaigns?session_id=${sessionId}`),
        apiCallJson(`/api/cdp/sessions/${sessionId}/segments`)
      ]);

      setCampaigns(campaignsRes.campaigns || []);
      setSegments(segmentsRes.segments || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteCampaign = async (campaignId) => {
    if (!window.confirm('Execute this campaign now?')) return;

    setExecuting(prev => new Set(prev).add(campaignId));

    try {
      await apiCallJson(`/api/campaigns/${campaignId}/execute`, {
        method: 'POST'
      });

      alert('Campaign execution started!');
      loadData();
    } catch (err) {
      alert(`Failed to execute campaign: ${err.message}`);
    } finally {
      setExecuting(prev => {
        const next = new Set(prev);
        next.delete(campaignId);
        return next;
      });
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await apiCallJson(`/api/campaigns/${campaignId}/pause`, {
        method: 'POST'
      });
      loadData();
    } catch (err) {
      alert(`Failed to pause campaign: ${err.message}`);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign? This action cannot be undone.')) return;

    try {
      await apiCallJson(`/api/campaigns/${campaignId}`, {
        method: 'DELETE'
      });
      loadData();
    } catch (err) {
      alert(`Failed to delete campaign: ${err.message}`);
    }
  };

  const CampaignCreateModal = () => {
    const [formData, setFormData] = useState({
      segment_id: '',
      campaign_name: '',
      description: '',
      primary_channel: 'whatsapp',
      fallback_channels: [],
      message_template: '',
      schedule_time: '',
      sample_percentage: 100,
      conversion_tracking_enabled: true,
      cost_per_unit: 0.05
    });

    const handleSubmit = async (e) => {
      e.preventDefault();

      try {
        await apiCallJson('/api/campaigns', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            session_id: sessionId,
            ...formData,
            schedule_time: formData.schedule_time || null
          })
        });

        setShowCreateModal(false);
        loadData();
        alert('Campaign created successfully!');
      } catch (err) {
        alert(`Failed to create campaign: ${err.message}`);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold">Create New Campaign</h2>
            <button
              onClick={() => setShowCreateModal(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Segment Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Segment *
              </label>
              <select
                value={formData.segment_id}
                onChange={(e) => setFormData({ ...formData, segment_id: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a segment</option>
                {segments.map(seg => (
                  <option key={seg.segment_id} value={seg.segment_id}>
                    {seg.segment_name} ({seg.customer_count?.toLocaleString()} customers)
                  </option>
                ))}
              </select>
            </div>

            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Name *
              </label>
              <input
                type="text"
                value={formData.campaign_name}
                onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
                required
                placeholder="e.g., Summer Sale 2024"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder="Campaign description and goals..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Primary Channel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Channel *
              </label>
              <select
                value={formData.primary_channel}
                onChange={(e) => setFormData({ ...formData, primary_channel: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="sms">SMS</option>
                <option value="email">Email</option>
                <option value="rcs">RCS</option>
                <option value="voice">Voice</option>
              </select>
            </div>

            {/* Message Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Template *
              </label>
              <textarea
                value={formData.message_template}
                onChange={(e) => setFormData({ ...formData, message_template: e.target.value })}
                required
                rows={4}
                placeholder="Hi {{name}}, don't miss our special offer..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Use {"{{field_name}}"} for personalization variables
              </p>
            </div>

            {/* Sample Percentage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sample Percentage: {formData.sample_percentage}%
              </label>
              <input
                type="range"
                min="1"
                max="100"
                value={formData.sample_percentage}
                onChange={(e) => setFormData({ ...formData, sample_percentage: parseFloat(e.target.value) })}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Target {Math.round((segments.find(s => s.segment_id === formData.segment_id)?.customer_count || 0) * formData.sample_percentage / 100).toLocaleString()} customers
              </p>
            </div>

            {/* Schedule Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Time (optional)
              </label>
              <input
                type="datetime-local"
                value={formData.schedule_time}
                onChange={(e) => setFormData({ ...formData, schedule_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to save as draft
              </p>
            </div>

            {/* Cost per Unit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cost per Message
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  step="0.001"
                  value={formData.cost_per_unit}
                  onChange={(e) => setFormData({ ...formData, cost_per_unit: parseFloat(e.target.value) })}
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Conversion Tracking */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="conversion_tracking"
                checked={formData.conversion_tracking_enabled}
                onChange={(e) => setFormData({ ...formData, conversion_tracking_enabled: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="conversion_tracking" className="ml-2 text-sm text-gray-700">
                Enable conversion tracking
              </label>
            </div>

            {/* Submit Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
              >
                Create Campaign
              </button>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const CampaignCard = ({ campaign }) => {
    const isExecuting = executing.has(campaign.campaign_id);
    const deliveryRate = campaign.sent_count > 0
      ? (campaign.delivered_count / campaign.sent_count) * 100
      : 0;

    const getStatusBadge = (status) => {
      const badges = {
        draft: 'bg-gray-100 text-gray-800',
        scheduled: 'bg-blue-100 text-blue-800',
        running: 'bg-yellow-100 text-yellow-800',
        paused: 'bg-orange-100 text-orange-800',
        completed: 'bg-green-100 text-green-800',
        failed: 'bg-red-100 text-red-800',
        cancelled: 'bg-gray-100 text-gray-600'
      };
      return badges[status] || badges.draft;
    };

    return (
      <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {campaign.campaign_name}
            </h3>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(campaign.status)}`}>
                {campaign.status}
              </span>
              <span className="text-xs text-gray-500">
                {campaign.segment_name}
              </span>
            </div>
          </div>
        </div>

        {campaign.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
            {campaign.description}
          </p>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500">Target</p>
            <p className="text-lg font-bold text-gray-900">
              {campaign.target_count?.toLocaleString() || 0}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Sent</p>
            <p className="text-lg font-bold text-gray-900">
              {campaign.sent_count?.toLocaleString() || 0}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Delivered</p>
            <p className="text-lg font-bold text-green-600">
              {campaign.delivered_count?.toLocaleString() || 0}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Failed</p>
            <p className="text-lg font-bold text-red-600">
              {campaign.failed_count?.toLocaleString() || 0}
            </p>
          </div>
        </div>

        {campaign.sent_count > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Delivery Rate</span>
              <span className="font-semibold">{deliveryRate.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${deliveryRate >= 80 ? 'bg-green-500' : deliveryRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${Math.min(deliveryRate, 100)}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">
              {campaign.primary_channel}
            </span>
            {campaign.schedule_time && (
              <span className="text-xs text-gray-500 flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                {new Date(campaign.schedule_time).toLocaleDateString()}
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {campaign.status === 'draft' || campaign.status === 'scheduled' ? (
              <>
                <button
                  onClick={() => handleExecuteCampaign(campaign.campaign_id)}
                  disabled={isExecuting}
                  className="p-2 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                  title="Execute campaign"
                >
                  {isExecuting ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => {
                    setSelectedCampaign(campaign);
                    setShowEditModal(true);
                  }}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  title="Edit campaign"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteCampaign(campaign.campaign_id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                  title="Delete campaign"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            ) : campaign.status === 'running' ? (
              <button
                onClick={() => handlePauseCampaign(campaign.campaign_id)}
                className="p-2 text-orange-600 hover:bg-orange-50 rounded"
                title="Pause campaign"
              >
                <Pause className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={() => setSelectedCampaign(campaign)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                title="View details"
              >
                <Eye className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !campaigns.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Campaign Management</h1>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Campaign
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Campaign Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampaigns.map((campaign) => (
            <CampaignCard key={campaign.campaign_id} campaign={campaign} />
          ))}
        </div>

        {filteredCampaigns.length === 0 && !loading && (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No campaigns found</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create Your First Campaign
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && <CampaignCreateModal />}
    </div>
  );
};

export default CampaignDashboard;